import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import FormControl from 'react-bootstrap/FormControl';
import { connect } from 'react-redux';
import { flowRight as compose } from 'lodash';
import cx from 'classnames';

import Loader from '../../../Common/Loader';
import { saveFeeCategory } from '../../../../actions/siteadmin/FeeCategory/saveFeeCategory';
import { deleteFeeCategory } from '../../../../actions/siteadmin/FeeCategory/deleteFeeCategory';

import s from './FeeCategoryList.css';
import bt from '../../../../components/commonStyle.css';

/**
 * FeeCategoryList — admin screen to view and edit the Gov Levy / HMO
 * Contribution / Fuel Index rows. Renders an inline-editable table so
 * the admin can change `value` and toggle `isActive` per row without
 * leaving the page.
 *
 * Conservative on JS syntax (no `??` per project convention). Uses
 * `||` for defaults and optional chaining (`?.`) which is already
 * widely used in this codebase.
 */
class FeeCategoryList extends React.Component {
  static defaultProps = {
    feeCategories: {
      loading: true,
    },
  };

  constructor(props) {
    super(props);
    this.state = {
      // Per-row local draft values keyed by id.
      drafts: {},
      savingId: null,
    };
  }

  getRowValue(item) {
    const draft = this.state.drafts[item.id];
    if (draft && draft.value !== undefined && draft.value !== null) {
      return draft.value;
    }
    return item.value !== undefined && item.value !== null ? item.value : '';
  }

  getRowActive(item) {
    const draft = this.state.drafts[item.id];
    if (draft && draft.isActive !== undefined && draft.isActive !== null) {
      return draft.isActive;
    }
    return item.isActive === true;
  }

  handleValueChange = (id, e) => {
    const next = Object.assign({}, this.state.drafts);
    next[id] = Object.assign({}, next[id] || {}, { value: e.target.value });
    this.setState({ drafts: next });
  };

  handleActiveChange = (id, e) => {
    const isActive = e.target.value === 'true';
    const next = Object.assign({}, this.state.drafts);
    next[id] = Object.assign({}, next[id] || {}, { isActive: isActive });
    this.setState({ drafts: next });
  };

  getRowPayTo(item) {
    const draft = this.state.drafts[item.id];
    if (draft && draft.payTo !== undefined && draft.payTo !== null) {
      return draft.payTo;
    }
    return item.payTo === 'driver' ? 'driver' : 'platform';
  }

  handlePayToChange = (id, e) => {
    const payTo = e.target.value === 'driver' ? 'driver' : 'platform';
    const next = Object.assign({}, this.state.drafts);
    next[id] = Object.assign({}, next[id] || {}, { payTo: payTo });
    this.setState({ drafts: next });
  };

  handleSave = async (item) => {
    const valueRaw = this.getRowValue(item);
    const valueNum = Number(valueRaw);
    if (isNaN(valueNum) || valueNum < 0) {
      // Trust DOM validation messaging is enough; bail without dispatch.
      return;
    }

    const payload = {
      id: item.id,
      name: item.name,
      type: item.type,
      value: valueNum,
      currency: item.currency || 'NGN',
      scope: item.scope || 'global',
      applyOrder:
        item.applyOrder === null || item.applyOrder === undefined
          ? 0
          : item.applyOrder,
      isActive: this.getRowActive(item),
      payTo: this.getRowPayTo(item),
    };

    this.setState({ savingId: item.id });
    await this.props.saveFeeCategory(payload);
    this.setState({ savingId: null });
  };

  handleDeactivate = async (item) => {
    if (!item || !item.id) return;
    if (
      typeof window !== 'undefined' &&
      typeof window.confirm === 'function' &&
      !window.confirm('Deactivate "' + item.name + '"?')
    ) {
      return;
    }
    this.setState({ savingId: item.id });
    await this.props.deleteFeeCategory(item.id);
    this.setState({ savingId: null });
  };

  describeType(type) {
    if (type === 'flat') return 'Flat';
    if (type === 'percentage') return 'Percentage (%)';
    if (type === 'per_km') return 'Per km';
    return type || '';
  }

  render() {
    const { feeCategories } = this.props;
    const loading = feeCategories && feeCategories.loading;
    const data =
      (feeCategories &&
        feeCategories.getFeeCategories &&
        feeCategories.getFeeCategories.results) ||
      [];

    return (
      <div className={cx(s.root, 'whiteDropdown', 'bgBlackTwo')}>
        <div className={s.heading}>Multi-Category Platform Fees</div>
        <div className={s.note}>
          These fees are applied to every trip in addition to the existing
          rider service fee, gated by the <strong>MULTI_FEES_ENABLED</strong>{' '}
          server env flag. Changes here take effect on the next trip
          calculation.
        </div>

        {loading && <Loader type="circle" />}

        {!loading && (
          <div
            className={cx(
              s.tableCss,
              'tableCss',
              'tableSticky',
              'NewResponsiveTable',
              'topScrollbarTable',
            )}
          >
            <Table
              className={cx('table', 'topScrollbarTableContent')}
              striped
              bordered
              hover
            >
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Currency</th>
                  <th>Order</th>
                  <th>Pay To</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 && (
                  <tr>
                    <td colSpan="9">No fee categories defined.</td>
                  </tr>
                )}
                {data.map((item) => {
                  const isSaving = this.state.savingId === item.id;
                  const isActiveNow = this.getRowActive(item);
                  return (
                    <tr key={item.id} className={s.row}>
                      <td>{item.id}</td>
                      <td>{item.name}</td>
                      <td>{this.describeType(item.type)}</td>
                      <td>
                        <FormControl
                          type="number"
                          step="0.01"
                          min="0"
                          className={s.valueInput}
                          value={String(this.getRowValue(item))}
                          onChange={(e) => this.handleValueChange(item.id, e)}
                          disabled={isSaving}
                        />
                      </td>
                      <td>{item.currency || 'NGN'}</td>
                      <td>{item.applyOrder}</td>
                      <td>
                        <select
                          className={bt.selectInput}
                          value={this.getRowPayTo(item)}
                          onChange={(e) => this.handlePayToChange(item.id, e)}
                          disabled={isSaving}
                        >
                          <option value="platform">Platform</option>
                          <option value="driver">Driver</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className={bt.selectInput}
                          value={String(isActiveNow)}
                          onChange={(e) => this.handleActiveChange(item.id, e)}
                          disabled={isSaving}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </td>
                      <td>
                        <Button
                          className={cx(s.saveBtn, bt.btnPrimary, 'btnPrimaryDark')}
                          onClick={() => this.handleSave(item)}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => this.handleDeactivate(item)}
                          disabled={isSaving || !item.isActive}
                        >
                          Deactivate
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    );
  }
}

const mapDispatch = {
  saveFeeCategory,
  deleteFeeCategory,
};

const mapState = (state) => ({});

export default compose(
  withStyles(s, bt),
  connect(mapState, mapDispatch),
)(FeeCategoryList);
